/**
 * Secure cross-platform key/value store.
 *
 * On iOS / Android (Capacitor): uses the device Keychain via
 * `capacitor-secure-storage-plugin`. Survives app reinstall (iOS),
 * encrypted at rest, and matches what the privacy policy advertises.
 *
 * On web / desktop / simulator: falls back to localStorage so dev and
 * the admin web console keep working unchanged.
 *
 * The module also maintains an in-memory cache so the synchronous
 * helpers in `lib/api.js` don't have to become async — we hydrate the
 * cache once at app boot via `hydrate()` before any API call fires.
 */
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

const KEYS = ['smarthoops_auth_token', 'smarthoops_device_id'];
const isNative = (() => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
})();

const cache = new Map();

const safeLocal = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  remove(k) { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};

async function nativeGet(key) {
  try {
    const r = await SecureStoragePlugin.get({ key });
    return r?.value ?? null;
  } catch {
    return null;
  }
}

async function nativeSet(key, value) {
  try { await SecureStoragePlugin.set({ key, value }); } catch { /* ignore */ }
}

async function nativeRemove(key) {
  try { await SecureStoragePlugin.remove({ key }); } catch { /* ignore */ }
}

/**
 * Read every known key from secure storage (or localStorage on web)
 * into the in-memory cache. Call once during app bootstrap before
 * any synchronous reader hits `getItem()`.
 */
export async function hydrate() {
  for (const k of KEYS) {
    let v = null;
    if (isNative) {
      v = await nativeGet(k);
      // One-time migration: copy any legacy localStorage value into
      // the keychain so existing TestFlight users don't get logged out
      // on the first launch of the new build.
      if (v == null) {
        const legacy = safeLocal.get(k);
        if (legacy != null) {
          v = legacy;
          await nativeSet(k, legacy);
          safeLocal.remove(k);
        }
      }
    } else {
      v = safeLocal.get(k);
    }
    if (v != null) cache.set(k, v);
  }
}

/** Synchronous read of a hydrated key. Returns null if not found. */
export function getItem(key) {
  return cache.has(key) ? cache.get(key) : null;
}

/** Synchronous-looking write — updates cache immediately, persists in
 *  background. Reads are consistent right away. */
export function setItem(key, value) {
  cache.set(key, value);
  if (isNative) { nativeSet(key, value); } else { safeLocal.set(key, value); }
}

export function removeItem(key) {
  cache.delete(key);
  if (isNative) { nativeRemove(key); } else { safeLocal.remove(key); }
}
